
import { useState, useEffect } from 'react';
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Trophy, Star, Target, Zap } from 'lucide-react';
import { 
  getUserXP, 
  getUserBadges, 
  getLeaderboard, 
  getActiveWeeklyChallenges,
  getUserChallengeProgress,
  getOrikiTitle 
} from '@/utils/gamificationUtils';
import { UserXP, UserBadge, WeeklyChallenge, UserChallengeProgress } from './types';

export default function GamificationDashboard() {
  const { user } = useAuth();
  const [userXP, setUserXP] = useState<UserXP | null>(null);
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [challenges, setChallenges] = useState<WeeklyChallenge[]>([]);
  const [challengeProgress, setChallengeProgress] = useState<UserChallengeProgress[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  const fetchUserData = async () => {
    if (!user) return;
    
    try {
      const [xpData, badgesData, leaderboardData, challengesData, progressData] = await Promise.all([
        getUserXP(user.id),
        getUserBadges(user.id),
        getLeaderboard(10),
        getActiveWeeklyChallenges(),
        getUserChallengeProgress(user.id)
      ]);
      
      setUserXP(xpData);
      setUserBadges(badgesData);
      setLeaderboard(leaderboardData);
      setChallenges(challengesData);
      setChallengeProgress(progressData);
    } catch (error) {
      console.error('Error fetching gamification data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="h-20 bg-gray-200"></CardHeader>
            <CardContent className="h-32 bg-gray-100"></CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const currentLevel = userXP?.level || 1;
  const currentXP = userXP?.total_xp || 0;
  const xpForCurrentLevel = (currentLevel - 1) * 100;
  const xpForNextLevel = currentLevel * 100;
  const xpProgress = ((currentXP - xpForCurrentLevel) / (xpForNextLevel - xpForCurrentLevel)) * 100;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {/* User Level & XP */}
      <Card className="border-omoduduwa-terracotta/20">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-omoduduwa-terracotta">
            <Star className="w-5 h-5" />
            Your Oriki Level
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-omoduduwa-terracotta">
                Level {currentLevel}
              </div>
              <div className="text-sm text-gray-600">
                {getOrikiTitle(currentLevel)}
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{currentXP} XP</span>
                <span>{xpForNextLevel} XP</span>
              </div>
              <Progress value={xpProgress} className="h-2" />
              <div className="text-xs text-gray-500 text-center">
                {xpForNextLevel - currentXP} XP to next level
              </div>
            </div>

            {/* User Badges */}
            <div className="space-y-2">
              <div className="font-medium text-sm">Your Badges</div>
              <div className="flex flex-wrap gap-1">
                {userBadges.slice(0, 3).map((badge) => (
                  <Badge key={badge.id} variant="secondary" className="text-xs">
                    {badge.badge_type === 'omo_oduduwa' ? '👑' : '🏆'} 
                    Level {badge.badge_level}
                  </Badge>
                ))}
                {userBadges.length === 0 && (
                  <span className="text-xs text-gray-500">No badges yet</span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card className="border-omoduduwa-terracotta/20">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-omoduduwa-terracotta">
            <Trophy className="w-5 h-5" />
            Top Contributors
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {leaderboard.slice(0, 5).map((user, index) => (
              <div key={user.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${
                    index === 0 ? 'text-yellow-500' :
                    index === 1 ? 'text-gray-400' :
                    index === 2 ? 'text-amber-600' : 'text-gray-500'
                  }`}>
                    #{index + 1}
                  </span>
                  <span className="text-sm truncate">
                    {user.member_name}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {user.total_xp} XP
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Weekly Challenges */}
      <Card className="border-omoduduwa-terracotta/20">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-omoduduwa-terracotta">
            <Target className="w-5 h-5" />
            Weekly Challenges
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {challenges.slice(0, 2).map((challenge) => {
              const progress = challengeProgress.find(p => p.challenge_id === challenge.id);
              const isCompleted = progress?.completed || false;
              
              return (
                <div key={challenge.id} className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium">{challenge.title}</div>
                      <div className="text-xs text-gray-500 line-clamp-2">
                        {challenge.description}
                      </div>
                    </div>
                    <Badge variant={isCompleted ? "default" : "secondary"} className="ml-2">
                      <Zap className="w-3 h-3 mr-1" />
                      {challenge.xp_reward} XP
                    </Badge>
                  </div>
                  
                  {isCompleted && (
                    <div className="text-xs text-green-600 font-medium">
                      ✅ Completed!
                    </div>
                  )}
                </div>
              );
            })}
            
            {challenges.length === 0 && (
              <div className="text-sm text-gray-500 text-center py-4">
                No active challenges
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
